import ApplicationController from "./application_controller"

export default class extends ApplicationController {
  static targets = ["alert", "firstName", "lastName", "email", "submit"]

  static values = {
    requiredEmail: Boolean,
    verifyUrl: String,
  }

  connect() {
    this.lookupTimer = null
    if (this.hasEmailTarget) this.validate()
  }

  disconnect() {
    clearTimeout(this.lookupTimer)
  }

  validate() {
    if (!this.hasEmailTarget || !this.hasFirstNameTarget || !this.hasLastNameTarget || !this.hasSubmitTarget) return
    const email = this.emailTarget.value.trim()
    const firstNamePresent = this.firstNameTarget.value.trim().length > 0
    const lastNamePresent = this.lastNameTarget.value.trim().length > 0
    const validEmail = this.isValidEmail(email)

    this.firstNameTarget.setCustomValidity(firstNamePresent ? "" : "First name is required.")
    this.lastNameTarget.setCustomValidity(lastNamePresent ? "" : "Last name is required.")
    this.emailTarget.setCustomValidity(validEmail ? "" : "Enter a valid email address.")
    this.firstNameTarget.classList.toggle("is-invalid", !firstNamePresent)
    this.lastNameTarget.classList.toggle("is-invalid", !lastNamePresent)
    this.emailTarget.classList.toggle("is-invalid", !validEmail)
    this.submitTarget.disabled = !(firstNamePresent && lastNamePresent && validEmail)
  }

  lookupEmail() {
    clearTimeout(this.lookupTimer)
    this.lookupTimer = setTimeout(() => this.fetchExistingUser(), 300)
  }

  async fetchExistingUser() {
    if (!this.hasEmailTarget || !this.hasAlertTarget) return
    const email = this.emailTarget.value.trim()
    if (!this.isValidEmail(email)) {
      this.hideAlert()
      this.resetNameFields()
      return
    }

    try {
      const response = await fetch(this.verifyUrlValue, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector("[name='csrf-token']")?.content,
        },
        body: JSON.stringify({ email }),
      })
      if (!response.ok) throw new Error(`HTTP Error: ${response.status}`)

      const data = await response.json()
      if (data.exists) {
        this.showMatch(data.first_name, data.last_name)
      } else {
        this.hideAlert()
        this.resetNameFields()
      }
    } catch (error) {
      console.error("Email lookup failed:", error)
    }
  }

  isValidEmail(email) {
    if (email === "") return !this.requiredEmailValue
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
  }

  showMatch(foundFirstName, foundLastName) {
    const firstMatches = this.firstNameTarget.value.trim().toLowerCase() === foundFirstName.trim().toLowerCase()
    const lastMatches = this.lastNameTarget.value.trim().toLowerCase() === foundLastName.trim().toLowerCase()

    this.alertTarget.replaceChildren()
    const message = document.createElement("strong")
    message.textContent = "User found with this email."
    this.alertTarget.append(message, document.createElement("br"))
    this.appendComparison("First name", this.firstNameTarget.value.trim(), foundFirstName, firstMatches)
    this.appendComparison("Last name", this.lastNameTarget.value.trim(), foundLastName, lastMatches)

    const applyButton = document.createElement("button")
    applyButton.type = "button"
    applyButton.className = "btn btn-sm btn-outline-secondary mt-2"
    applyButton.textContent = "Use found names"
    applyButton.addEventListener("click", () => this.applyFoundNames(foundFirstName, foundLastName))
    this.alertTarget.append(applyButton)
    this.alertTarget.classList.remove("d-none")
  }

  appendComparison(label, entered, found, matches) {
    const row = document.createElement("div")
    const status = document.createElement("span")
    status.className = matches ? "text-success" : "text-danger"
    status.textContent = matches ? "matches" : "does not match"
    row.textContent = `${label} `
    row.append(status, ` (entered: "${entered || "(blank)"}", found: "${found}")`)
    this.alertTarget.append(row)
  }

  applyFoundNames(firstName, lastName) {
    this.firstNameTarget.value = firstName
    this.lastNameTarget.value = lastName
    this.firstNameTarget.readOnly = true
    this.lastNameTarget.readOnly = true
    this.validate()
  }

  resetNameFields() {
    this.firstNameTarget.readOnly = false
    this.lastNameTarget.readOnly = false
  }

  hideAlert() {
    this.alertTarget.replaceChildren()
    this.alertTarget.classList.add("d-none")
  }

  submit(event) {
    if (!this.hasSubmitTarget) return
    if (this.hasEmailTarget) this.emailTarget.value = this.emailTarget.value.trim()
    this.validate()
    if (this.submitTarget.disabled) event.preventDefault()
  }
}
